/** A file to upload plus the folder chain (relative to the drop root) it belongs in. */
export interface PendingFile {
    file: File;
    dirPath: string[];
}

/**
 * Must run synchronously inside the drop event — the DataTransferItemList
 * is only valid until the first await.
 */
export function captureDataTransfer(dt: DataTransfer): { entries: FileSystemEntry[]; files: File[] } {
    const entries: FileSystemEntry[] = [];
    const items = dt.items;
    if (items) {
        for (let i = 0; i < items.length; i++) {
            const it = items[i];
            if (!it || it.kind !== "file" || typeof it.webkitGetAsEntry !== "function") continue;
            const entry = it.webkitGetAsEntry();
            if (entry) entries.push(entry);
        }
    }
    return { entries, files: Array.from(dt.files) };
}

function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
    // readEntries returns at most ~100 entries per call — keep reading until empty.
    return new Promise((resolve, reject) => {
        const all: FileSystemEntry[] = [];
        const read = () =>
            reader.readEntries((batch) => {
                if (batch.length === 0) resolve(all);
                else {
                    all.push(...batch);
                    read();
                }
            }, reject);
        read();
    });
}

function entryToFile(entry: FileSystemFileEntry): Promise<File> {
    return new Promise((resolve, reject) => entry.file(resolve, reject));
}

async function walkEntry(entry: FileSystemEntry, dirPath: string[], out: PendingFile[]): Promise<void> {
    if (entry.isFile) {
        out.push({ file: await entryToFile(entry as FileSystemFileEntry), dirPath });
    } else if (entry.isDirectory) {
        const dir = entry as FileSystemDirectoryEntry;
        const children = await readAllEntries(dir.createReader());
        for (const c of children) await walkEntry(c, [...dirPath, dir.name], out);
    }
}

/** Walks dropped files/folders, preserving the directory tree. */
export async function walkEntries(entries: FileSystemEntry[]): Promise<PendingFile[]> {
    const out: PendingFile[] = [];
    for (const e of entries) await walkEntry(e, [], out);
    return out;
}

/** `<input webkitdirectory>` gives flat files with a relative path — rebuild the tree. */
export function filesFromFolderInput(list: FileList): PendingFile[] {
    return Array.from(list).map((file) => ({
        file,
        dirPath: (file.webkitRelativePath || file.name).split("/").slice(0, -1).filter(Boolean)
    }));
}
